import { api } from "./client";

/**
 * The Buyer Outreach tab (backend name: BuyerSequence).
 *
 * Copy-and-log only: nothing here sends an email or a text. A LOG click writes
 * ordinary communication entries through the same create path the
 * Communications tab uses, and Undo is the existing
 * `DELETE /communications/{id}` (see `communicationsApi`).
 *
 * The tab never reads or writes buyer status, milestones, follow-up tasks or
 * stale flags (G1).
 */

export type BuyerSequenceStepKey =
  | "intro"
  | "prequal-intro"
  | "follow-up-1"
  | "follow-up-2"
  | "follow-up-3"
  | "submitted"
  | "check-in";

/** Rail order (G5). Mirrors `BuyerSequenceSteps.All`. */
export const BUYER_SEQUENCE_STEPS: readonly BuyerSequenceStepKey[] = [
  "intro",
  "prequal-intro",
  "follow-up-1",
  "follow-up-2",
  "follow-up-3",
  "submitted",
  "check-in",
];

export type BuyerSequenceSelectionMode = "listing" | "none" | "unselected";
export type BuyerSequenceWordingSource = "team" | "personal";
export type BuyerSequenceBlockKey = "text" | "subject" | "email" | "signature";
export type BuyerSequenceEditTarget =
  | "none"
  | "buyer-contact"
  | "profile"
  | "associated-listings"
  | "call-hours"
  | "submissions";
export type BuyerSequenceZoneSource = "state" | "area-code" | "user";

export const BUYER_SEQUENCE_SIGNATURE_KEY = "buyer.sequence.signature";
export const buyerSequenceTemplateKey = (step: BuyerSequenceStepKey) =>
  `buyer.sequence.${step}`;

export interface BuyerSequenceSegmentDto {
  text: string;
  /** The merge field that produced this run; null for the template's own words. */
  field?: string | null;
  /** A required field with no value — its placeholder must never be copied. */
  missing?: boolean;
}

export interface BuyerSequenceRenderedDto {
  /** Exactly what Copy puts on the clipboard. */
  plain: string;
  segments: BuyerSequenceSegmentDto[];
  /** Non-empty ⇒ Copy is disabled on the card that shows this block. */
  missingRequiredFields: string[];
  /**
   * Every merge field this render reached, in first-use order. A field inside
   * a section that did not render is not here; one that fell back or was left
   * out IS. The "Pulled from this record" card lists exactly these (L3).
   */
  usedFields: string[];
}

export interface BuyerSequenceStepDto {
  key: BuyerSequenceStepKey;
  name: string;
  requiresListing: boolean;
  /** Most recent time this step was logged from the tab; null = no marker. */
  lastSentAtUtc: string | null;
}

export interface BuyerSequenceListingOptionDto {
  listingId: string;
  label: string;
}

export interface BuyerSequenceBrokerDto {
  name: string;
  firm: string | null;
}

export interface BuyerSequenceFieldRowDto {
  field: string;
  label: string;
  value: string;
  source: string;
  usedFallback: boolean;
  missing: boolean;
  editTarget: BuyerSequenceEditTarget;
}

export interface BuyerSequenceWordingRefDto {
  source: BuyerSequenceWordingSource;
  version: number;
  teamChangedSincePersonal?: boolean;
}

export interface BuyerSequenceMessagesDto {
  text: BuyerSequenceRenderedDto;
  subject: BuyerSequenceRenderedDto;
  emailBody: BuyerSequenceRenderedDto;
  wording: BuyerSequenceWordingRefDto;
}

export interface BuyerSequenceZoneDto {
  timeZoneId: string;
  label: string;
  source: BuyerSequenceZoneSource;
  sourceDescription: string;
}

export interface BuyerSequenceStateDto {
  buyerLeadId: string;
  buyerFirstName: string | null;
  buyerPhone: string | null;
  buyerEmail: string | null;
  stepKey: BuyerSequenceStepKey;
  steps: BuyerSequenceStepDto[];
  listings: BuyerSequenceListingOptionDto[];
  selectionMode: BuyerSequenceSelectionMode;
  selectedListingId: string | null;
  broker: BuyerSequenceBrokerDto | null;
  submissionId: string | null;
  messages: BuyerSequenceMessagesDto | null;
  unavailableReason: string | null;
  signature: BuyerSequenceRenderedDto;
  signatureWording: BuyerSequenceWordingRefDto;
  fields: BuyerSequenceFieldRowDto[];
  missingProfileFields: string[];
  buyerZone: BuyerSequenceZoneDto;
}

/** What "Message about" asks the server for. `listingId` wins; `noListing`
 *  selects "No listing (internal referral)"; neither lets the server choose. */
export interface BuyerSequenceSelection {
  listingId?: string | null;
  noListing?: boolean;
}

export interface LogBuyerSequenceRequest {
  stepKey: BuyerSequenceStepKey;
  /** Null for "No listing". */
  listingId: string | null;
  /** The exact text copied; null when the text box is not ticked. */
  textBody: string | null;
  emailSubject: string | null;
  /** The exact email copied — body plus signature when it was included. */
  emailBody: string | null;
  wordingSource: BuyerSequenceWordingSource;
  wordingVersion: number;
}

export interface BuyerSequenceLogResultDto {
  entryIds: string[];
  /** "Text + email", "Text" or "Email". */
  summary: string;
  loggedAtUtc: string;
}

export interface PreviewBuyerSequenceRequest {
  templateKey: string;
  blocks: Record<string, string>;
  listingId: string | null;
  noListing?: boolean;
}

export interface BuyerSequenceFindingDto {
  blockKey: string;
  message: string;
}

export interface BuyerSequencePreviewDto {
  blocks: Record<string, BuyerSequenceRenderedDto>;
  findings: BuyerSequenceFindingDto[];
}

// ── Wording ──────────────────────────────────────────────────────────────────

export interface BuyerSequenceWordingBlockDto {
  blockKey: BuyerSequenceBlockKey;
  label: string;
  defaultText: string;
  teamDraftText: string | null;
  teamText: string;
  personalText: string | null;
}

export interface BuyerSequenceTeamVersionDto {
  version: number;
  publishedAtUtc: string;
  publishedByName: string | null;
  note: string | null;
}

export interface BuyerSequenceWordingDto {
  templateKey: string;
  name: string;
  blocks: BuyerSequenceWordingBlockDto[];
  /** 0 = the code default. */
  teamVersion: number;
  teamVersions: BuyerSequenceTeamVersionDto[];
  hasTeamDraft: boolean;
  hasPersonal: boolean;
  personalRevision: number | null;
  personalBasedOnTeamVersion: number | null;
  personalSavedAtUtc: string | null;
  teamChangedSincePersonal: boolean;
  teamChangedAtUtc: string | null;
  canEditTeam: boolean;
}

export interface BuyerSequenceWordingSummaryDto {
  templateKey: string;
  name: string;
  teamVersion: number;
  hasTeamDraft: boolean;
  hasPersonal: boolean;
  teamChangedSincePersonal: boolean;
  teamChangedAtUtc: string | null;
}

export interface BuyerSequenceFieldInfoDto {
  name: string;
  label: string;
  fallback: string | null;
  required: boolean;
  isFlag: boolean;
  description: string;
}

export interface BuyerSequenceCallHoursDto {
  /** Minutes after local midnight in the user's own zone. */
  startMinutes: number;
  endMinutes: number;
  timeZoneId: string;
  timeZoneLabel: string;
  isDefault: boolean;
}

export const buyerSequenceApi = {
  getState: (
    buyerId: string,
    step: BuyerSequenceStepKey | null,
    selection: BuyerSequenceSelection,
  ) =>
    api.get<BuyerSequenceStateDto>(`/buyers/${buyerId}/sequence`, {
      step: step ?? undefined,
      listingId: selection.listingId ?? undefined,
      noListing: selection.noListing ? true : undefined,
    }),

  log: (buyerId: string, body: LogBuyerSequenceRequest) =>
    api.post<BuyerSequenceLogResultDto>(`/buyers/${buyerId}/sequence/log`, body),

  preview: (buyerId: string, body: PreviewBuyerSequenceRequest) =>
    api.post<BuyerSequencePreviewDto>(`/buyers/${buyerId}/sequence/preview`, body),

  listWording: () => api.get<BuyerSequenceWordingSummaryDto[]>("/buyer-sequence/wording"),

  getWording: (templateKey: string) =>
    api.get<BuyerSequenceWordingDto>(`/buyer-sequence/wording/${encodeURIComponent(templateKey)}`),

  saveTeamDraft: (templateKey: string, blockKey: BuyerSequenceBlockKey, text: string | null) =>
    api.put<BuyerSequenceWordingDto>(
      `/buyer-sequence/wording/${encodeURIComponent(templateKey)}/team/draft`,
      { blockKey, text },
    ),

  discardTeamDraft: (templateKey: string) =>
    api.delete<BuyerSequenceWordingDto>(
      `/buyer-sequence/wording/${encodeURIComponent(templateKey)}/team/draft`,
    ),

  publishTeam: (templateKey: string, note: string | null) =>
    api.post<BuyerSequenceWordingDto>(
      `/buyer-sequence/wording/${encodeURIComponent(templateKey)}/team/publish`,
      { note },
    ),

  rollbackTeam: (templateKey: string, version: number) =>
    api.post<BuyerSequenceWordingDto>(
      `/buyer-sequence/wording/${encodeURIComponent(templateKey)}/team/rollback/${version}`,
    ),

  savePersonal: (templateKey: string, blocks: Record<string, string>) =>
    api.put<BuyerSequenceWordingDto>(
      `/buyer-sequence/wording/${encodeURIComponent(templateKey)}/mine`,
      { blocks },
    ),

  deletePersonal: (templateKey: string) =>
    api.delete<BuyerSequenceWordingDto>(
      `/buyer-sequence/wording/${encodeURIComponent(templateKey)}/mine`,
    ),

  listFields: () => api.get<BuyerSequenceFieldInfoDto[]>("/buyer-sequence/fields"),

  getCallHours: () => api.get<BuyerSequenceCallHoursDto>("/buyer-sequence/call-hours"),

  saveCallHours: (startMinutes: number, endMinutes: number) =>
    api.put<BuyerSequenceCallHoursDto>("/buyer-sequence/call-hours", {
      startMinutes,
      endMinutes,
    }),
};
